import React, { useState, useEffect, useRef } from "react";

import moment from "moment";
import ViewPharmacyModal from "./ViewPharmacyModal";
import { Virtuoso } from "react-virtuoso";
import { ReactComponent as FilterPoligon } from "../../../assets/icons/filterPoligon.svg";
import APIVariables from "../../../services/api-variables";
import Loader from "../../common/Loader/Loader";
import SearchGray from "../../../assets/icons/searchGray.svg";
import { ReactComponent as ArrowRightBlue } from "../../../assets/icons/arrowRightBlue.svg";
import Axios from "../../../services/Axios";
import FilterDataViewPatients from "../ViewPatients/FilterDataViewPatients";
import { handleCatch } from "../../../shared/helpers";

const COL_CLASS = "min-w-0 flex-[0_0_24%] px-1 text-center text-base text-[#3C3C3C]";

function FilterPopover({ children, popoverRef, className = "" }) {
  return (
    <div
      ref={popoverRef}
      className={`wrapper-filter absolute top-9 z-10 w-56 bg-[#f8f8f8] shadow-[0_0_40px_rgba(0,0,0,0.2)] ${className}`}
    >
      {children}
    </div>
  );
}

function FilterSearchInput({ value, onChange, placeholder, showSearchIcon, "data-testid": testId }) {
  return (
    <div className="relative bg-[#f8f8f8] px-4 py-2">
      <input
        type="search"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="h-[29px] w-full rounded border border-[#ccc] bg-white px-2.5 text-base text-[#3C3C3C] shadow-[0_0_10px_rgba(0,0,0,0.03)] outline-none focus:border-[#00567a] focus:ring-1 focus:ring-[#00567a]/30"
        data-testid={testId}
      />
      {showSearchIcon && (
        <img
          src={SearchGray}
          alt=""
          className="pointer-events-none absolute right-6 top-1/2 h-4 w-4 -translate-y-1/2"
        />
      )}
    </div>
  );
}

function ColumnHeader({ children, onClick, className = "", "data-testid": testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testId}
      className={`wrapper-filter flex items-center justify-center gap-1 text-base font-bold text-[#3C3C3C] ${onClick ? "cursor-pointer" : ""
        } ${className}`}
    >
      {children}
    </Tag>
  );
}

const PharmacyList = () => {
  const debounceTime = 500;
  const [filters, setFilters] = useState(new Map());
  const [showFilter, setShowFilter] = useState("");
  const pharmacyNameRef = useRef();
  const registeredProducts = useRef();
  const postCodeRef = useRef();
  const [isFetching, setIsFetching] = useState(false);
  const [items, setItems] = useState([]);
  const [existMoreItems, setExistMoreItems] = useState(false);
  const [isOpenViewModal, setIsOpenViewModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [product, setProduct] = useState();

  function handleOutsideClick(event) {
    const { target } = event;

    const refs = [pharmacyNameRef, postCodeRef, registeredProducts];

    const contains = refs.some((ref) => {
      try {
        const svgClass = target?.getAttribute("class");
        if (svgClass === "rs-icon") {
          return true;
        }

        return (
          ref?.current?.contains(target) ||
          target?.className?.includes("wrapper-filter") ||
          target?.className?.includes("rs-calendar") ||
          target?.className?.includes("rs-calendar-header-month-toolbar") ||
          target?.className?.includes("rs-picker-toolbar") ||
          target?.className?.includes("rs-btn")
        );
      } catch {
        return false;
      }
    });

    if (!contains) setShowFilter("");
  }

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const onChangeShowFilter = (type) => {
    if (type === showFilter) return setShowFilter("");
    setShowFilter(type);
  };

  /* eslint-disable */
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      getPharmacies(false);
    }, debounceTime);

    return () => {
      clearTimeout(delayDebounce);
    };
  }, [filters, searchValue]);

  async function getPharmacies(fetchMore) {
    setIsFetching(true);
    let filterString = !fetchMore ? "?" : "";
    if (searchValue.length > 0) {
      filterString += `search_lookup=${searchValue}`;
    }

    [...filters.keys()].forEach((key) => {
      const currentFilter = filters.get(key);
      currentFilter.forEach((filter) => {
        if (!Boolean(filter.dataType)) {
          filterString += `&${key}=${filter.id}`;
        } else {
          filterString += `&${key}=${filter.value}`;
        }
      });
    });

    const link = fetchMore
      ? existMoreItems
      : APIVariables.PHARMACY_LIST + filterString;

    try {
      const res = await Axios.get(link);

      setIsLoading(false);

      if (fetchMore) setItems([...items, ...res.data]);
      else setItems(res.data);

      const areMoreMoreItems = !!res.data.next;

      if (areMoreMoreItems) {
        setExistMoreItems(res.data.next);
      } else {
        setExistMoreItems(false);
      }
    } catch (error) {
      handleCatch(error);
    }

    setIsFetching(false);
  }

  const onChangeFilters = (type, obj, isRemoveFromLabel) => {
    const filtersMap = new Map(filters);
    const typesArrays = ["string", "date"];
    if (typesArrays.includes(obj.dataType)) {
      if (String(obj.value).length < 1) return deleteFilter(type);
      if (isRemoveFromLabel) return deleteFilter(type);
      filtersMap.set(type, [obj]);
    } else {
      if (filtersMap.has(type)) {
        const currentFilter = filtersMap.get(type);
        const isFilter = currentFilter.find((filter) => filter.id === obj.id);
        if (isFilter) {
          return deleteFilter(type, obj);
        }
        filtersMap.set(type, [...currentFilter, obj]);
      } else {
        filtersMap.set(type, [obj]);
      }
    }

    setFilters(filtersMap);
  };

  const deleteFilter = (type, obj) => {
    const filtersMap = new Map(filters);
    if (!obj) {
      filtersMap.delete(type);
      return setFilters(filtersMap);
    }

    const currentFilter = filtersMap.get(type);
    const newArrayFilters = currentFilter.filter(
      (filter) => filter.id !== obj.id
    );
    if (newArrayFilters.length < 1) {
      filtersMap.delete(type);
    } else {
      filtersMap.set(type, newArrayFilters);
    }

    setFilters(filtersMap);
  };

  const getStringFilter = (key) => {
    if (filters.has(key)) return filters.get(key)[0].value;
    return "";
  };

  const showFilterSearchIcon = (key) => !getStringFilter(key);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div
        className="min-h-screen bg-[#f5f6f7] px-4 pb-10 pt-24 font-sans"
        data-testid="pharmacy-list-page"
      >
        <div className="mx-auto w-full min-w-[736px] max-w-6xl">
          <h1
            className="mb-6 text-center text-2xl font-bold text-[#3C3C3C]"
            data-testid="pharmacy-list-heading"
          >
            Registered Pharmacy Search
          </h1>

          <div
            className="mb-2.5 flex min-h-[46px] flex-wrap gap-1"
            data-testid="pharmacy-list-active-filters"
          >
            {filters &&
              [...filters.keys()].map((key) => {
                const currentFilter = filters.get(key);
                return currentFilter.map((filter) => (
                  <FilterDataViewPatients
                    key={key + filter.value}
                    keyRefer={key}
                    filter={filter}
                    onChangeFilters={onChangeFilters}
                  />
                ));
              })}
          </div>

          <div
            className="overflow-hidden rounded-t-md bg-white shadow-[0_4px_20px_rgba(0,0,0,0.1)]"
            data-testid="pharmacy-list-table"
          >
            <div className="flex items-center justify-between border-b border-[#dcdcdc] bg-black/5 px-5 py-3.5">
              <div className="relative w-64 shrink-0 sm:w-72">
                <input
                  type="search"
                  placeholder="Search pharmacy name, postcode, registered product..."
                  onChange={(event) => {
                    setSearchValue(event.target.value);
                  }}
                  value={searchValue}
                  className="w-full min-w-[180px] rounded border border-[#ccc] bg-white py-3 pl-10 pr-5 text-base font-normal text-[#8c8c8c] outline-none placeholder:text-base placeholder:font-normal placeholder:text-[#8c8c8c] focus:border-[#00567a] focus:ring-1 focus:ring-[#00567a]/30 sm:min-w-[489px]"
                  data-testid="pharmacy-list-search-input"
                />
                <img
                  src={SearchGray}
                  alt=""
                  className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2"
                />
              </div>
            </div>

            <div className="border-b border-[#dcdcdc]">
              <div className="flex min-h-[60px] w-full items-center px-8 py-7">
                <div className={`relative ${COL_CLASS}`}>
                  <ColumnHeader
                    onClick={() => onChangeShowFilter("name")}
                    data-testid="pharmacy-list-filter-name-button"
                  >
                    Pharmacy Name <FilterPoligon className="h-3 w-3 shrink-0" />
                  </ColumnHeader>
                  {showFilter === "name" && (
                    <FilterPopover
                      popoverRef={pharmacyNameRef}
                      className="-ml-[70px] left-1/2"
                    >
                      <FilterSearchInput
                        value={getStringFilter("name") || ""}
                        onChange={(e) => {
                          onChangeFilters("name", {
                            value: e.target.value,
                            label: "Pharmacy Name",
                            dataType: "string",
                          });
                        }}
                        placeholder="Find pharmacy name..."
                        showSearchIcon={showFilterSearchIcon("name")}
                        data-testid="pharmacy-list-filter-name-input"
                      />
                    </FilterPopover>
                  )}
                </div>

                <div className={`relative ${COL_CLASS}`}>
                  <ColumnHeader
                    onClick={() => onChangeShowFilter("postal_code")}
                    data-testid="pharmacy-list-filter-postcode-button"
                  >
                    Postcode <FilterPoligon className="h-3 w-3 shrink-0" />
                  </ColumnHeader>
                  {showFilter === "postal_code" && (
                    <FilterPopover popoverRef={postCodeRef} className="left-0">
                      <FilterSearchInput
                        value={getStringFilter("postal_code") || ""}
                        onChange={(e) => {
                          onChangeFilters("postal_code", {
                            value: e.target.value,
                            label: "Postcode",
                            dataType: "string",
                          });
                        }}
                        placeholder="Find postcode..."
                        showSearchIcon={showFilterSearchIcon("postal_code")}
                        data-testid="pharmacy-list-filter-postcode-input"
                      />
                    </FilterPopover>
                  )}
                </div>

                <div className={`relative ${COL_CLASS}`}>
                  <ColumnHeader
                    onClick={() => onChangeShowFilter("products")}
                    data-testid="pharmacy-list-filter-products-button"
                  >
                    Registered Product{" "}
                    <FilterPoligon className="h-3 w-3 shrink-0" />
                  </ColumnHeader>
                  {showFilter === "products" && (
                    <FilterPopover
                      popoverRef={registeredProducts}
                      className="left-0 ml-[30px]"
                    >
                      <FilterSearchInput
                        value={getStringFilter("products") || ""}
                        onChange={(e) => {
                          onChangeFilters("products", {
                            value: e.target.value,
                            label: "Registered Product",
                            dataType: "string",
                          });
                        }}
                        placeholder="Find product name..."
                        showSearchIcon={showFilterSearchIcon("products")}
                        data-testid="pharmacy-list-filter-products-input"
                      />
                    </FilterPopover>
                  )}
                </div>

                <div className={COL_CLASS}>
                  <ColumnHeader>Status</ColumnHeader>
                </div>

                <div className="flex w-8 shrink-0 justify-end" aria-hidden />
              </div>

              <Virtuoso
                endReached={() => {
                  if (existMoreItems && !isFetching) {
                    getPharmacies(true);
                  }
                }}
                style={{ height: "400px" }}
                data={items}
                itemContent={(index) => {
                  const row = items[index];
                  const regDate = row?.reg_valid_until;
                  const statusLabel = !regDate
                    ? "Unknown"
                    : moment().isAfter(moment(regDate))
                      ? "Expired Registration"
                      : "Registered";

                  return (
                    <div
                      key={row?.id ?? index}
                      role="button"
                      tabIndex={0}
                      className="flex w-full cursor-pointer items-center border-b border-[#dcdcdc] px-8 py-7 transition-colors hover:bg-black/5"
                      data-testid={`pharmacy-list-row-${row?.id ?? index}`}
                      onClick={() => {
                        setIsOpenViewModal({
                          state: true,
                          product: row.products,
                          id: row.id,
                        });
                        setProduct(row.products);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setIsOpenViewModal({
                            state: true,
                            product: row.products,
                            id: row.id,
                          });
                          setProduct(row.products);
                        }
                      }}
                    >
                      <div className={`${COL_CLASS} font-medium uppercase`}>
                        {row?.name}
                      </div>
                      <div className={COL_CLASS}>{row?.postal_code}</div>
                      <div className={`${COL_CLASS} wrap-break-word`}>
                        {row?.products}
                      </div>
                      <div className={COL_CLASS}>{statusLabel}</div>
                      <div className="flex w-8 shrink-0 justify-end text-[#00567a]">
                        <ArrowRightBlue className="h-5 w-5" />
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <ViewPharmacyModal
        isOpen={isOpenViewModal?.state}
        onRequestClose={() => setIsOpenViewModal(null)}
        ID={isOpenViewModal?.id}
        product={product}
        regValidUntil={
          items.find(
            (item) =>
              item.id === isOpenViewModal?.id &&
              item.products === isOpenViewModal?.product
          )?.reg_valid_until
        }
      />
    </>
  );
};

export default PharmacyList;
